import { Injectable } from '@angular/core';
import { DELIVERY_API, ORDER_FULFILLMENT_API } from '@shared/constants/api.constants';
import { HttpService } from '../http/http.service';
import {
  createOrderResponseModel,
  deliveryResponseModel,
  OrderListDetails,
  OrderSearchQueryParams,
  Orders,
} from '@shared/models/delivery/delivery.model';
import { map, Observable } from 'rxjs';
import { mapProductLookup } from '@shared/utilities/product-options.utils';
import { normalizeDeliveryRows, recomputeDeliveryStatusTotals } from '@shared/utilities/delivery-row.utils';
import { CustomerAddressProductLookup } from '@shared/models/customer-address/customer-address.model';
import { CustomerPortalProductOption } from '@shared/models/product/product-filter.model';

@Injectable({
  providedIn: 'root'
})
export class DeliveryService {

  protected constructor(
    private http: HttpService,
  ) { }

  private searchOrders(params: OrderSearchQueryParams): Observable<deliveryResponseModel> {
    return this.http.get<deliveryResponseModel>(DELIVERY_API.DELIVERY_TABLE, {
      ...params,
    }).pipe(
      map((response) => ({
        ...response,
        orderList: normalizeDeliveryRows(response.orderList ?? []),
      }))
    );
  }

  getDelivery(params: OrderSearchQueryParams): Observable<deliveryResponseModel> {
    return this.searchOrders(params);
  }

  getDeliveryHistory(params: {
    clientId: string;
    customerId: number;
    sort: string;
    pageNumber: number;
    pageSize: number;
    search: string;
    statusFilter?: string;
    dateDeliveredStartDate?: string | null;
    dateDeliveredEndDate?: string | null;
  }): Observable<deliveryResponseModel> {
    const requestParams: OrderSearchQueryParams = {
      clientId: params.clientId,
      customerId: params.customerId,
      sort: params.sort,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      search: params.search,
      ...(params.statusFilter ? { statusFilter: params.statusFilter } : {}),
      ...(params.dateDeliveredStartDate ? { dateDeliveredStartDate: params.dateDeliveredStartDate } : {}),
      ...(params.dateDeliveredEndDate ? { dateDeliveredEndDate: params.dateDeliveredEndDate } : {}),
    };

    return this.searchOrders(requestParams);
  }

  createDelivery(payload: createOrderResponseModel): Observable<any> {
    return this.http.post(ORDER_FULFILLMENT_API.CREATE_ORDER, payload);
  }

  getDeliveryDetails(params: {
    clientId: string,
    customerId: number,
    orderId: string
  }): Observable<Orders> {
    return this.http.get(DELIVERY_API.DELIVERY_DETAILS, params);
  }

  getDeliveryStatusLists(params: {
    clientId: string;
    customerId: number;
    pageNumber: number;
    pageSize: number;
    sort: string;
    search: string;
    productIdFilter?: number | string;
    statusFilter: string;
    mode?: string;
    groupId?: number;
    siteId?: string;
  }): Observable<{
    deliveredTotal?: number;
    forDeliveryTotal?: number;
    orderList: OrderListDetails[];
    recordsFiltered: number;
    recordsTotal: number;
    requestedTotal?: number;
    scheduledTotal?: number;
  }> {
    const requestParams: OrderSearchQueryParams = {
      clientId: params.clientId,
      customerId: params.customerId,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      sort: params.sort,
      search: params.search,
      statusFilter: params.statusFilter,
      ...(params.mode ? { mode: params.mode } : {}),
      ...(params.groupId != null ? { groupId: params.groupId } : {}),
      ...(params.siteId ? { siteId: params.siteId } : {}),
    };

    if (params.productIdFilter != null && params.productIdFilter !== '') {
      requestParams.productIdFilter = Number(params.productIdFilter);
    }

    return this.searchOrders(requestParams).pipe(
      map((response) => {
        const orderList = response.orderList ?? [];
        return {
          ...response,
          orderList,
          ...recomputeDeliveryStatusTotals(orderList),
        };
      })
    );
  }

  getProductsLists(params: {
    clientId: string,
    customerId: string | number,
    customerAddressId?: number,
    shippingAddressId?: number,
    type?: string,
    sort?: string,
  }): Observable<{
    recordsFiltered: number,
    productList: CustomerPortalProductOption[]
  }> {
    const customerAddressId = params.customerAddressId ?? params.shippingAddressId;

    return this.http.get<CustomerAddressProductLookup[]>(DELIVERY_API.PRODUCTS_LIST, {
      clientId: params.clientId,
      customerId: params.customerId,
      ...(customerAddressId != null ? { customerAddressId } : {}),
    }).pipe(
      map((products) => {
        const productList = mapProductLookup(products);
        return {
          recordsFiltered: productList.length,
          productList,
        };
      })
    );
  }
}
